<template>
    <div class="weeduc-admin-page weeduc-forum-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="loading ? $t('admin.forum.title') : forum.title || $t('admin.forum.title')"
                    :subtitle="headerSubtitle"
                    :hasMargin="false"
                />
                <p v-if="!loading" class="weeduc-forum-meta">
                    {{ $t('admin.forum.viewComments') }}: {{ forum.commentsQty || messages.length || 0 }}
                    <span>·</span>
                    {{ $t('admin.forum.viewBy') }}: {{ forum.userName || '—' }}
                    <span>·</span>
                    {{ $t('admin.forum.viewDate') }}: {{ formatDateShort(forum.dateInsert) }}
                </p>
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    v-if="!loading && isAdmin && forum.status"
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="showConfirmModal = true"
                >
                    <fa :icon="['fas', 'lock']" class="me-1" />
                    {{ $t('admin.forum.closeForum') }}
                </v-btn>
                <span
                    v-if="!loading && !forum.status"
                    class="weeduc-forum-status is-closed"
                >
                    {{ $t('admin.forum.statusClosed') }}
                </span>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="loading" class="weeduc-admin-page__panel weeduc-forum-panel">
                <div class="weeduc-forum-loading">
                    <Loader :isActive="false" />
                </div>
            </div>

            <div v-else class="weeduc-admin-page__panel weeduc-forum-panel">
                <div v-if="uniqueUsers.length" class="weeduc-forum-filters">
                    <button
                        type="button"
                        class="weeduc-forum-chip"
                        :class="{ 'is-active': selectedUser === '' }"
                        @click="selectUser('')"
                    >
                        {{ $t('common.all') }}
                    </button>
                    <button
                        v-for="user in uniqueUsers"
                        :key="user"
                        type="button"
                        class="weeduc-forum-chip"
                        :class="{ 'is-active': selectedUser === user }"
                        @click="selectUser(user)"
                    >
                        {{ user }}
                    </button>
                </div>

                <div class="weeduc-forum-chat">
                    <div class="weeduc-forum-messages" ref="chatMessages">
                        <template v-if="filteredMessages.length">
                            <article
                                v-for="message in filteredMessages"
                                :key="message.id"
                                class="weeduc-forum-message"
                            >
                                <header class="weeduc-forum-message__header">
                                    <div class="weeduc-forum-message__user">
                                        <img
                                            v-if="message.userFinalPhoto"
                                            :src="message.userFinalPhoto"
                                            class="weeduc-forum-message__avatar"
                                            :alt="message.userName"
                                        />
                                        <span
                                            v-else
                                            class="weeduc-forum-message__avatar weeduc-forum-message__avatar--initials"
                                            aria-hidden="true"
                                        >
                                            {{ userInitials(message.userName) }}
                                        </span>
                                        <div class="weeduc-forum-message__identity">
                                            <span class="weeduc-forum-message__name">
                                                {{ message.userName }}
                                            </span>
                                            <time class="weeduc-forum-message__time">
                                                {{ formatDate(message.dateInsert) }}
                                            </time>
                                        </div>
                                    </div>
                                    <v-btn
                                        v-if="isAdmin"
                                        icon
                                        variant="text"
                                        size="small"
                                        color="error"
                                        class="weeduc-forum-message__delete"
                                        :aria-label="$t('admin.forum.deleteMessageAria')"
                                        @click="deleteMessage(message.id)"
                                    >
                                        <fa icon="fas fa-trash" />
                                    </v-btn>
                                </header>
                                <div
                                    class="weeduc-forum-message__body"
                                    v-html="message.comment"
                                />
                            </article>
                        </template>
                        <div v-else class="weeduc-forum-empty">
                            <fa icon="far fa-comments" />
                            <p>{{ $t('admin.doubts.noMessages') }}</p>
                        </div>
                    </div>

                    <div v-if="forum.status" class="weeduc-forum-composer">
                        <v-textarea
                            v-model="newMessage"
                            :placeholder="$t('admin.forum.replyPlaceholder')"
                            variant="outlined"
                            density="compact"
                            class="weeduc-admin-v-field"
                            hide-details
                            rows="3"
                            auto-grow
                            :disabled="sending"
                            @keydown.ctrl.enter="sendMessage"
                        />
                        <div class="weeduc-forum-composer__actions">
                            <v-btn
                                color="primary"
                                variant="flat"
                                rounded="lg"
                                class="text-none weeduc-forum-composer__send"
                                :loading="sending"
                                :disabled="sending || !newMessage.trim()"
                                @click="sendMessage"
                            >
                                <fa icon="fas fa-paper-plane" class="me-1" />
                                {{ $t('common.send') }}
                            </v-btn>
                        </div>
                    </div>
                    <div v-else class="weeduc-forum-closed">
                        <fa icon="fas fa-lock" />
                        <p>{{ $t('admin.forum.statusClosed') }}</p>
                    </div>
                </div>
            </div>
        </div>

        <v-dialog v-model="showConfirmModal" max-width="520">
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ $t('common.confirm') }}</h2>
                        <p class="corporate-dialog__subtitle">
                            {{ $t('admin.forum.closeForumConfirm') }}
                        </p>
                    </div>
                </v-card-title>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        @click="showConfirmModal = false"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        @click="confirmCloseForumStatus"
                    >
                        Confirmar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import ApiForum from '@/utils/forum';
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';

export default {
    name: 'AdminForumView',
    components: { TitlePage, Loader },
    data() {
        return {
            messages: [],
            filteredMessages: [],
            newMessage: '',
            loading: true,
            sending: false,
            isAdmin: true,
            selectedUser: '',
            showConfirmModal: false,
        };
    },
    computed: {
        ...mapState(['token', 'forum']),
        uniqueUsers() {
            return [...new Set(this.messages.map((message) => message.userName))];
        },
        headerSubtitle() {
            if (this.loading) return this.$t('admin.forum.loadingForum');
            return this.forum.description || this.$t('admin.forum.discussionFallback');
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '—';
            return new Date(date).toLocaleString('pt-BR');
        },
        formatDateShort(date) {
            if (!date) return '—';
            return new Date(date).toLocaleDateString('pt-BR');
        },
        userInitials(name) {
            const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return '?';
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        },
        selectUser(user) {
            this.selectedUser = user;
            this.filterMessages();
        },
        async getMessages() {
            const id = this.$route.params.slug;
            try {
                const response = await ApiForum.getMessageById(this.token, id);
                this.messages = response.sort(
                    (a, b) => new Date(a.dateInsert) - new Date(b.dateInsert)
                );
                this.filterMessages();
                this.$nextTick(() => this.scrollToBottom());
            } catch (error) {
                console.error('Erro ao carregar mensagens:', error);
            } finally {
                this.loading = false;
            }
        },
        filterMessages() {
            if (this.selectedUser) {
                this.filteredMessages = this.messages.filter(
                    (message) => message.userName === this.selectedUser
                );
            } else {
                this.filteredMessages = this.messages;
            }
        },
        async deleteMessage(id) {
            try {
                await ApiForum.deleteMessageById(this.token, id);
                await this.getMessages();
            } catch (error) {
                console.error('Erro ao deletar mensagem:', error);
            }
        },
        async sendMessage() {
            if (!this.newMessage.trim() || this.sending) return;

            this.sending = true;
            try {
                const payload = {
                    comment: this.newMessage,
                    idForum: this.$route.params.slug,
                };

                await ApiForum.creteNewMessage(this.token, payload);
                this.newMessage = '';
                await this.getMessages();
                this.scrollToBottom();
            } catch (error) {
                console.error('Erro ao enviar mensagem:', error);
            } finally {
                this.sending = false;
            }
        },
        scrollToBottom() {
            setTimeout(() => {
                const container = this.$refs.chatMessages;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 100);
        },
        async confirmCloseForumStatus() {
            await this.toggleForumStatus();
            this.showConfirmModal = false;
        },
        async toggleForumStatus() {
            const id = this.$route.params.slug;
            const payload = {
                status: 0,
                idUser: this.forum.idUser,
                userName: this.forum.userName,
                dateInsert: this.forum.dateInsert,
                description: this.forum.description,
                title: this.forum.title,
                plainDescription: this.forum.plainDescription,
                commentsQty: this.forum.commentsQty,
            };
            try {
                await ApiForum.editForumById(this.token, id, payload);
                this.$router.push('/dashboard/forum');
            } catch (error) {
                console.error('Erro ao atualizar status do fórum:', error);
            }
        },
    },
    created() {
        this.getMessages();
    },
};
</script>

<style scoped>
@import url('@/css/weeduc/admin/dashboard-weflix-shell.css');
@import url('@/css/weeduc/admin/corporate.css');

.weeduc-forum-page :deep(.weeduc-admin-page__shell) {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.weeduc-forum-meta {
    margin: 0.45rem 0 0;
    font-size: 0.8125rem;
    color: #5c6370;
    line-height: 1.45;
}

.weeduc-forum-meta span {
    margin: 0 0.35rem;
    opacity: 0.7;
}

.weeduc-forum-status {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
}

.weeduc-forum-status.is-closed {
    background: #fdecea;
    color: #c62828;
}

.weeduc-forum-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow: hidden;
}

.weeduc-forum-loading {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    padding: 2rem;
}

.weeduc-forum-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem 1.35rem 0.85rem;
    border-bottom: 1px solid #e8eaf0;
    background: #fafbff;
}

.weeduc-forum-chip {
    border: 1px solid #e8eaf0;
    background: #fff;
    color: #5c6370;
    border-radius: 999px;
    padding: 0.35rem 0.9rem;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.weeduc-forum-chip:hover {
    border-color: rgba(31, 30, 97, 0.25);
    color: #1f1e61;
}

.weeduc-forum-chip.is-active {
    background: #1f1e61;
    border-color: #1f1e61;
    color: #fff;
}

.weeduc-forum-chat {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.weeduc-forum-messages {
    flex: 1;
    min-height: 280px;
    max-height: calc(100vh - 380px);
    overflow-y: auto;
    padding: 1.15rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: #f6f7fb;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 transparent;
}

.weeduc-forum-messages::-webkit-scrollbar {
    width: 6px;
}

.weeduc-forum-messages::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
}

.weeduc-forum-message {
    width: 100%;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 0.85rem 0.95rem 0.95rem;
    animation: weeducForumFadeIn 0.25s ease-out;
}

.weeduc-forum-message__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.weeduc-forum-message__user {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
}

.weeduc-forum-message__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    background: #eef0fc;
    flex-shrink: 0;
}

.weeduc-forum-message__avatar--initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #1f1e61;
    background: #e8eaf8;
}

.weeduc-forum-message__identity {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
}

.weeduc-forum-message__name {
    font-weight: 650;
    font-size: 0.9rem;
    color: #1a1d26;
    line-height: 1.25;
}

.weeduc-forum-message__time {
    font-size: 0.75rem;
    color: #6b7280;
}

.weeduc-forum-message__delete {
    flex-shrink: 0;
    margin: -0.2rem -0.25rem 0 0;
}

.weeduc-forum-message__body {
    margin: 0.65rem 0 0 2.95rem;
    padding: 0;
    border: 0;
    background: transparent;
    line-height: 1.55;
    font-size: 0.9375rem;
    color: #1a1d26;
    word-break: break-word;
}

.weeduc-forum-composer {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.25rem 1.15rem;
    border-top: 1px solid #e8eaf0;
    background: #fff;
}

.weeduc-forum-composer :deep(.v-textarea) {
    width: 100%;
}

.weeduc-forum-composer__actions {
    display: flex;
    justify-content: flex-end;
}

.weeduc-forum-composer__send {
    min-height: 40px;
}

.weeduc-forum-composer__send :deep(svg) {
    color: inherit;
}

.weeduc-forum-empty,
.weeduc-forum-closed {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 3.5rem 1.5rem;
    color: #5c6370;
    text-align: center;
    flex: 1;
}

.weeduc-forum-empty :deep(svg),
.weeduc-forum-closed :deep(svg) {
    font-size: 2.25rem;
    opacity: 0.55;
}

.weeduc-forum-closed {
    border-top: 1px solid #e8eaf0;
    padding: 1.25rem 1.35rem;
    flex: 0 0 auto;
}

@keyframes weeducForumFadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .weeduc-forum-messages {
        max-height: calc(100vh - 420px);
        padding: 1rem;
    }

    .weeduc-forum-message__body {
        margin-left: 0;
    }

    .weeduc-forum-composer__send {
        width: 100%;
    }

    .weeduc-admin-page__header-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
